import { Banner, Link, useDebounceCallback } from '@farol-ds/react'
import {
  PersistentAlertBannerIcon,
  PersistentAlertBannerLink,
} from '@jusbrasil-web/shared-apollo-client/types'
import { useUserSession } from '@jusbrasil-web/shared-user-session'
import { Fragment, useEffect, useState } from 'react'

import { usePersistentAlertBannerDispatcher } from '../../hooks'
import { jusbrasilExperiencePersistentAlertBannerPersisted } from '../../utils'
import { AlertIcon } from '../alert-icon'
import styles from './jusbrasil-experience.module.scss'

interface JusbrasilExperienceAlertProps {
  text?: string | null
  icon?: PersistentAlertBannerIcon | null
  link?: PersistentAlertBannerLink | null
}

export function JusbrasilExperienceAlert(props: JusbrasilExperienceAlertProps) {
  const user = useUserSession()
  const { sendTopbarEvent, ref } = usePersistentAlertBannerDispatcher<HTMLDivElement>()
  const [isVisible, setIsVisible] = useState(!!props)
  const { icon, text, link } = props ?? {}

  const handleVisibilityChange = (visible: boolean): void => {
    if (!visible) {
      jusbrasilExperiencePersistentAlertBannerPersisted.set(user?.pid?.toString())
      sendTopbarEvent('JusbrasilExperienceAlertClosed')
    }
    setIsVisible(visible)
  }

  const handleLinkClick = () => {
    sendTopbarEvent('JusbrasilExperienceAlertClicked')
  }

  useEffect(() => {
    if (isVisible) {
      sendTopbarEvent('JusbrasilExperienceAlertView')
    }
  }, [isVisible, sendTopbarEvent])

  const setJusbrasilExperienceAlertViewed = useDebounceCallback(
    () => jusbrasilExperiencePersistentAlertBannerPersisted.set(user?.pid?.toString()),
    5000
  )

  useEffect(() => {
    if (isVisible) setJusbrasilExperienceAlertViewed()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible])

  if (!isVisible) return null

  return (
    <Banner
      ref={ref}
      visible={isVisible}
      defaultVisible={false}
      level="app"
      type="discovery"
      onVisibleChange={handleVisibilityChange}
      className={styles.jusbrasilExperienceAlert}
    >
      <Banner.Content>
        <Banner.Title>
          {icon && (
            <Banner.Icon>
              <AlertIcon icon={icon} data-testid="jusbrasil-experience-alert-icon" />
            </Banner.Icon>
          )}
          {text}{' '}
          {link && (
            <Fragment>
              <Link
                href={link?.url}
                onClick={handleLinkClick}
                target="_blank"
                rel="noopener noreferrer"
              >
                {link?.label}
              </Link>
            </Fragment>
          )}
        </Banner.Title>
      </Banner.Content>
      <Banner.IconClose />
    </Banner>
  )
}
